import type {
  Microsoft365ConnectDto,
  Microsoft365DevicePollDto,
  Microsoft365DeviceStatus,
  Microsoft365Feature,
  Microsoft365SendBody,
  Microsoft365AsNewMailResultDto,
  Microsoft365SendResultDto,
  Microsoft365SignInError,
  Microsoft365StatusDto,
} from '@dbrain/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, isApiError } from './client.js';
import { useCapabilities } from '../experience.js';
import { useMe } from './hooks.js';

/**
 * Microsoft 365 for the person (Phase 15e): the state of the built-in connection (Bağlantılar, Ayarlar › Hesap ›
 * Bağlı hesaplar, the chat's notices), connecting (Microsoft's sign-in page, or a code on a plain-http install),
 * the person's own switch, removing the connection and asking the admin for consent. Every change reads the state
 * again under one key. No token or secret ever reaches the browser: the answers carry the state and the account's
 * e-mail address only.
 */
export const microsoft365Key = ['microsoft365'] as const;

export function useMicrosoft365Status(enabled = true) {
  const me = useMe();
  // Phase 16a: an edition without Microsoft 365 never asks (none of its routes exists there)
  const capabilities = useCapabilities();
  return useQuery({
    queryKey: microsoft365Key,
    queryFn: () => api<Microsoft365StatusDto>('GET', '/api/microsoft365'),
    enabled: enabled && Boolean(me.data) && capabilities.microsoft365,
    staleTime: 30_000,
  });
}

/** Where a sign-in comes back to and, for an upgrade (part 3), the features whose permissions Microsoft should ask for. */
export type Microsoft365ConnectInput = string | { returnTo: string; features?: readonly Microsoft365Feature[] };

/**
 * Starts a sign-in: Microsoft's page (`redirect`, the browser goes there) or a code to type there (`device`); the
 * server decides which. An upgrade names features ("Yeni izin gerekiyor · yeniden bağlan"), never scopes: the server
 * maps them. Refused (turned off meanwhile, unreachable): the state is read again.
 */
export function useMicrosoft365Connect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Microsoft365ConnectInput) => {
      const { returnTo, features } = typeof input === 'string' ? { returnTo: input, features: undefined } : input;
      return api<Microsoft365ConnectDto>('POST', '/api/microsoft365/connect', features && features.length > 0 ? { returnTo, features: [...features] } : { returnTo });
    },
    onError: () => void queryClient.invalidateQueries({ queryKey: microsoft365Key }),
  });
}

/**
 * Part 3, "Gönder" / "Oluştur ve davet et" on a write card: the hash the card was drawn from (the server's, echoed)
 * and what the person changed; the server rebuilds the text, the gateway writes it once. Never retried: the answer is
 * the outcome (sent, created, failed, unknown), or a refusal before anything was written (the card stays as it is).
 */
export function useMicrosoft365WriteSend(approvalId: string) {
  return useMutation({
    mutationFn: (body: Microsoft365SendBody) => api<Microsoft365SendResultDto>('POST', `/api/microsoft365/approvals/${encodeURIComponent(approvalId)}/send`, body),
    retry: false,
  });
}

/** v0.16c.6 (D-416): "Yeni mail olarak hazırla" on a reply card Microsoft could not open (a message that asks for the new card). */
export function useMicrosoft365AsNewMail(approvalId: string) {
  return useMutation({
    mutationFn: () => api<Microsoft365AsNewMailResultDto>('POST', `/api/microsoft365/approvals/${encodeURIComponent(approvalId)}/as-new-mail`, {}),
    retry: false,
  });
}

export function useMicrosoft365Disconnect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('DELETE', '/api/microsoft365/connection'),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: microsoft365Key }),
  });
}

/** The person's own on/off switch; off keeps the account. */
export function useMicrosoft365SetEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api<Microsoft365StatusDto>('PUT', '/api/microsoft365/enabled', { enabled }),
    onSuccess: (status) => queryClient.setQueryData(microsoft365Key, status),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: microsoft365Key }),
  });
}

/** Tells the firm's admins that Microsoft asked for their consent (the server allows one a hour). */
export function useMicrosoft365ConsentRequest() {
  return useMutation({ mutationFn: () => api<void>('POST', '/api/microsoft365/consent-request', {}) });
}

export interface Microsoft365DeviceStart {
  id: string;
  userCode: string;
  verificationUri: string;
  expiresAt: string;
  interval: number;
}

export interface Microsoft365DeviceState {
  /** `pending` while the person signs in on Microsoft's page */
  status: Microsoft365DeviceStatus;
  reason: Microsoft365SignInError | null;
  /** a poll the server refused (not a network hiccup, which is tried again) */
  error: unknown;
}

/** at least this long between two polls, whatever Microsoft said */
const MIN_INTERVAL_MS = 2_000;
/** network hiccups tolerated in a row before the dialog says so */
const HICCUPS = 3;

/**
 * The device code flow's waiting (RFC 8628): asks the server every `interval` seconds whether the person signed
 * in on Microsoft's page, the first time after one interval, until it is connected, declined, failed or the code
 * expired. Connected: the state is read again. Stops when the dialog closes (`device` null) or a new code comes.
 */
export function useMicrosoft365DevicePoll(device: Microsoft365DeviceStart | null): Microsoft365DeviceState {
  const queryClient = useQueryClient();
  const [state, setState] = useState<Microsoft365DeviceState>({ status: 'pending', reason: null, error: null });
  const client = useRef(queryClient);
  client.current = queryClient;
  useEffect(() => {
    setState({ status: 'pending', reason: null, error: null });
    if (!device) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let hiccups = 0;
    // Microsoft's interval (5 s by default); a missing or odd one never makes it poll faster
    const every = Math.max(MIN_INTERVAL_MS, Number.isFinite(device.interval) && device.interval > 0 ? device.interval * 1000 : 5_000);
    const expiresAt = new Date(device.expiresAt).getTime();
    const later = () => {
      timer = setTimeout(() => void tick(), every);
    };
    const tick = async () => {
      if (stopped) return;
      if (Number.isFinite(expiresAt) && Date.now() >= expiresAt) {
        setState({ status: 'expired', reason: null, error: null });
        return;
      }
      try {
        const result = await api<Microsoft365DevicePollDto>('POST', `/api/microsoft365/device/${encodeURIComponent(device.id)}/poll`, {});
        if (stopped) return;
        hiccups = 0;
        if (result.status === 'pending') {
          later();
          return;
        }
        if (result.status === 'connected') void client.current.invalidateQueries({ queryKey: microsoft365Key });
        setState({ status: result.status, reason: result.reason ?? null, error: null });
      } catch (error) {
        if (stopped) return;
        hiccups += 1;
        if ((isApiError(error, 'network_error') || isApiError(error, 'service_unavailable')) && hiccups < HICCUPS) {
          later();
          return;
        }
        setState({ status: 'failed', reason: null, error });
      }
    };
    later();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [device]);
  return state;
}
